/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.bodyPrefetch.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-BODYPREFETCH-001
 * PURPOSE: Background body-prefetch cascade — a module-scope concurrency-
 *          capped queue (EmailBodyPrefetchQueue) + the reactive
 *          useEmailBodyPrefetch hook that warms the top-N inbox rows so a
 *          click paints the reader synchronously.
 * WHY: Extracted from the useEmails god-hook (ARC EMAIL_HUB_LOADING_
 *      RESILIENCE phase 3, expand→migrate→contract). Self-contained
 *      subsystem — module singleton + thin hook — so the move is zero
 *      behaviour change. Public surface re-exported from ./useEmails.
 * RELATED:
 *   - ./useEmails.cache.ts: shares the LRU summary cache (skip-if-cached
 *     + write-back so useEmail seeds from cache on click).
 *   - ./useEmails.ts: re-exports the hook + test seams.
 *   - useEmailBodyPrefetch.test.ts: pins the cascade contract.
 */
import { useEffect } from 'react'
import { emailApi } from '../lib/api'
import type { Email } from './useEmails.types'
import { getCachedEmailSummary, rememberEmailSummary } from './useEmails.cache'

// ---------------------------------------------------------------------------
// Stack 13 MR-C — body-prefetch cascade queue.
//
// After ``useEmails`` Phase 2 lands the chip-enriched rows, the inbox
// is fully painted but the BODY of every row (except the auto-opened
// first one) lives only on the server. Hovering / clicking any of the
// remaining rows fires a fresh ``GET /api/emails/<id>/`` and the
// reader pane blanks for ~200-800 ms while the detail GET resolves.
//
// The cascade queue makes that click feel instant for the top-N rows
// by prefetching their bodies in the background:
//
//   - Top-N gate (default 10) — only the head of the list. Past row
//     10 the operator is making a deliberate scroll choice and the
//     200 ms detail-fetch latency is acceptable.
//   - Concurrency cap (2) — keeps the BE worker pool from drowning
//     in detail fetches while the inbox is also being polled.
//   - Already-cached skip — if ``g_emailListCache`` already has the
//     body (e.g. the user just opened that row), we don't re-fetch.
//   - Currently-open skip — the explicit ``useEmail(<currentId>)``
//     is already firing a detail fetch for the open row; the queue
//     leaves it alone.
//   - Visibility pause — ``document.hidden`` halts the queue
//     entirely. Resumes on ``visibilitychange`` back to visible.
//     The active fetches will complete on their own; queued items
//     wait until the operator returns.
//   - Idempotent enqueue — re-enqueueing the same id while a fetch
//     is pending or in flight is a no-op. Safe to call from a
//     useEffect that re-fires on every Phase-2 enrichment merge.
//   - Best-effort failure — a detail fetch that 401s / 5xx is
//     swallowed silently. The cascade is a polish layer; if the
//     row's body is missing on click, the explicit ``useEmail``
//     fetch will surface the real error there.
//
// Lives at module scope (not inside the React hook) so it survives
// re-mounts and so all consumers share one queue / one worker pool.
// The hook below (``useEmailBodyPrefetch``) is a thin reactive
// shim that calls ``enqueue`` whenever the input list changes.
// ---------------------------------------------------------------------------

const BODY_PREFETCH_MAX_CONCURRENCY = 2

class EmailBodyPrefetchQueue {
  private queue: string[] = []
  private active: Set<string> = new Set()
  private paused = false
  // Visibility listener installation is lazy — the queue might
  // never get used (e.g. legacy hosts that don't call enqueue).
  // Installing on first enqueue avoids polluting modules that
  // never need it.
  private visibilityWired = false

  enqueue(ids: string[]): void {
    this.ensureVisibilityWired()
    for (const id of ids) {
      if (typeof id !== 'string' || id === '') continue
      if (this.active.has(id)) continue
      if (this.queue.includes(id)) continue
      // ``g_emailListCache`` is the same module-scope cache the
      // ``useEmail`` detail fetch overwrites. Presence of body_html
      // OR body_text means a prior detail fetch already populated
      // the row; re-fetching would just burn quota.
      const cached = getCachedEmailSummary(id)
      if (cached && (cached.body_html != null || cached.body_text != null)) continue
      this.queue.push(id)
    }
    this.drain()
  }

  pause(): void {
    this.paused = true
  }

  resume(): void {
    this.paused = false
    this.drain()
  }

  clear(): void {
    // Queued items only — in-flight fetches complete on their own.
    // The fetchOne body checks ``paused`` before each detail GET
    // so a clear() right before a navigation will at most leak the
    // active 2 requests, which is acceptable.
    this.queue = []
  }

  /** Test-only — exposes private state for assertions. NOT for
   *  production callers. */
  __debugState(): {
    queued: string[]
    active: string[]
    paused: boolean
    maxConcurrency: number
  } {
    return {
      queued: [...this.queue],
      active: [...this.active],
      paused: this.paused,
      maxConcurrency: BODY_PREFETCH_MAX_CONCURRENCY,
    }
  }

  private ensureVisibilityWired(): void {
    if (this.visibilityWired) return
    if (typeof document === 'undefined') return
    this.visibilityWired = true
    document.addEventListener('visibilitychange', () => {
      // ``visibilitychange`` fires on BOTH transitions; check the
      // current state to decide. We don't need a focus listener
      // here — the queue doesn't care about Cmd-Tab vs alt-tab,
      // only about whether the tab is foregrounded.
      if (document.hidden) this.pause()
      else this.resume()
    })
  }

  private drain(): void {
    if (this.paused) return
    if (typeof document !== 'undefined' && document.hidden) return
    while (
      this.active.size < BODY_PREFETCH_MAX_CONCURRENCY &&
      this.queue.length > 0
    ) {
      const id = this.queue.shift()
      if (id === undefined) break
      this.active.add(id)
      // Fire-and-track. The .finally drains the next slot even if
      // the fetch threw — a hung BE shouldn't permanently wedge
      // the queue at concurrency=2.
      this.fetchOne(id).finally(() => {
        this.active.delete(id)
        this.drain()
      })
    }
  }

  private async fetchOne(id: string): Promise<void> {
    // Re-check pause / visibility right before the network hit —
    // the operator might have backgrounded the tab between when
    // this id was dequeued and when we got CPU. If so, drop the
    // fetch silently; a future resume() will re-enqueue the same
    // ids on the next ``useEmailBodyPrefetch`` re-render.
    if (this.paused) return
    if (typeof document !== 'undefined' && document.hidden) return
    try {
      const result = await emailApi<Email>(`/api/emails/${id}/`)
      // Overwriting the cache entry here is the whole point — the
      // next ``useEmail(<id>)`` hook mount will seed-from-cache
      // and paint header + body synchronously, completing the
      // commit-then-fill rendering contract for the top-N rows.
      rememberEmailSummary(result)
    } catch {
      // Silent failure — see class comment. The cascade is
      // best-effort; we don't toast or even console.log because
      // the operator will see the real error if they click the
      // row and the explicit useEmail fetch reports it.
    }
  }
}

// Module-scope singleton — see queue class comment for rationale.
const g_bodyPrefetchQueue = new EmailBodyPrefetchQueue()

/** Test-only escape hatch — clears the queue + resumes it so each
 *  test starts from a known state. Mirrors
 *  ``__resetEmailListCacheForTests``; not part of the public
 *  surface. */
export function __resetBodyPrefetchQueueForTests(): void {
  g_bodyPrefetchQueue.clear()
  g_bodyPrefetchQueue.resume()
}

/** Test-only — inspect the queue's private state. */
export function __getBodyPrefetchQueueStateForTests() {
  return g_bodyPrefetchQueue.__debugState()
}

export interface UseEmailBodyPrefetchOptions {
  /** Skip prefetching this id (e.g. the currently-open email — the
   *  explicit ``useEmail`` hook is already fetching it). */
  skipId?: string | null
  /** Cap on how many rows from the head of ``emailIds`` are
   *  considered. Default 10 — large enough that the operator's
   *  next 9 clicks all feel instant, small enough that we don't
   *  prefetch 200 bodies the operator will never read. */
  topN?: number
  /** Master kill switch — pass ``false`` to disable the cascade
   *  for a specific surface. The cascade is on by default for
   *  the inbox; opt-out is rare (long-lived TrashPage / SentPage
   *  where bodies are large + click-through rate is low). */
  enabled?: boolean
}

/** Stack 13 MR-C — background body-prefetch hook.
 *
 *  Schedule reactive enqueues against the module-scope
 *  ``g_bodyPrefetchQueue``. Hosts pass the FULL inbox list (after
 *  Phase 2 enrichment) and an optional ``skipId`` for the row
 *  currently being explicitly fetched.
 *
 *  Re-fires safely on every list change — the queue's idempotent
 *  enqueue means we don't double-fetch even if Phase 2 lands and
 *  the same ids come in twice.
 *
 *  Wire in InboxPage like::
 *
 *      const { emails } = useEmails({ ... })
 *      useEmailBodyPrefetch(
 *        emails.map(e => e.id),
 *        { skipId: selectedEmailId, topN: 10 },
 *      )
 *
 *  No-op when ``enabled === false``, when the input list is empty,
 *  or when the document is hidden at call-time (the queue will
 *  pick up the same ids on the next list change after visibility
 *  returns).
 */
export function useEmailBodyPrefetch(
  emailIds: string[],
  options: UseEmailBodyPrefetchOptions = {},
): void {
  const { skipId, topN = 10, enabled = true } = options
  useEffect(() => {
    if (!enabled) return
    if (!Array.isArray(emailIds) || emailIds.length === 0) return
    // Skip the explicit-current id BEFORE slicing — otherwise a
    // skipped id at position 3 effectively shrinks the top-N
    // window by 1.
    const candidates: string[] = []
    for (const id of emailIds) {
      if (candidates.length >= topN) break
      if (typeof id !== 'string' || id === '') continue
      if (id === skipId) continue
      candidates.push(id)
    }
    g_bodyPrefetchQueue.enqueue(candidates)
    // No cleanup — the queue is module-scope and the enqueue is
    // idempotent. Tearing down the inbox tab does NOT cancel
    // active fetches (best-effort prefetch), and any queued-only
    // ids will be naturally skipped by ``cached.body_html != null``
    // on the next mount.
  }, [emailIds, skipId, topN, enabled])
}
